/**
 * Telegram QR码授权组件
 * 支持扫码登录和2FA验证
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Modal, Button, Input, Spin, Alert, Space, Typography, Form, message } from 'antd'
import { QrcodeOutlined, ReloadOutlined, LockOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { useAuthStore } from '../store'

const { Text, Title } = Typography

interface TelegramQRAuthProps {
  visible: boolean
  onClose: () => void
  onSuccess: (user: TelegramUser) => void
  apiId: string
  apiHash: string
  sessionName: string
}

interface TelegramUser {
  user_id: string
  username: string | null
  first_name: string
  last_name: string | null
  phone: string | null
}

type AuthState = 'connecting' | 'authenticating' | 'qr_ready' | '2fa_required' | 'success' | 'error'

export default function TelegramQRAuth({
  visible,
  onClose,
  onSuccess,
  apiId,
  apiHash,
  sessionName
}: TelegramQRAuthProps) {
  const [state, setState] = useState<AuthState>('connecting')
  const [qrCode, setQrCode] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [password, setPassword] = useState('')
  const [encryptionKey, setEncryptionKey] = useState<string>('')
  const [telegramUser, setTelegramUser] = useState<TelegramUser | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const clientIdRef = useRef<string>('')
  const { token } = useAuthStore()

  // 生成唯一客户端ID
  useEffect(() => {
    clientIdRef.current = `tg_auth_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }, [])

  // 获取加密密钥
  useEffect(() => {
    if (visible) {
      fetch('/api/telegram/encryption-key')
        .then(res => res.json())
        .then(data => setEncryptionKey(data.key))
        .catch(err => console.error('Failed to get encryption key:', err))
    }
  }, [visible])

  // 加密密码
  const encryptPassword = useCallback(async (pwd: string): Promise<string> => {
    // 使用Web Crypto API进行加密
    // 注意: 这里简化了加密流程，实际生产环境应该使用更安全的加密方式
    const encoder = new TextEncoder()
    const data = encoder.encode(pwd)

    // 使用简单的Base64编码（实际应使用Fernet兼容的加密）
    // 由于前端无法直接使用Fernet，这里使用base64+key的简单方案
    // 生产环境应该使用更安全的加密库
    const combined = `${encryptionKey}:${pwd}`
    return btoa(combined)
  }, [encryptionKey])

  // 连接WebSocket
  const connect = useCallback(() => {
    if (!visible || !token) return

    setState('connecting')
    setError('')

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${protocol}//${window.location.host}/ws/telegram-auth/${clientIdRef.current}`

    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => {
      // 发送认证
      ws.send(JSON.stringify({
        type: 'auth',
        token: token
      }))
    }

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data)

      switch (data.type) {
        case 'auth_success':
          setState('authenticating')
          // 开始QR登录（若 apiId/apiHash 为空，后端会自动加载已保存凭据）
          const startMsg: Record<string, string> = { type: 'start', session_name: sessionName }
          if (apiId) startMsg.api_id = apiId
          if (apiHash) startMsg.api_hash = apiHash
          ws.send(JSON.stringify(startMsg))
          break

        case 'qr_code':
          setState('qr_ready')
          setQrCode(data.data)
          break

        case '2fa_required':
          setState('2fa_required')
          break

        case 'success':
          setState('success')
          setTelegramUser(data.user)
          message.success('Telegram账户授权成功')
          setTimeout(() => {
            onSuccess(data.user)
            handleClose()
          }, 1500)
          break

        case 'error':
          setState('error')
          setError(data.message)
          break

        case 'cancelled':
          handleClose()
          break
      }
    }

    ws.onerror = () => {
      setState('error')
      setError('WebSocket连接失败')
    }

    ws.onclose = () => {
      wsRef.current = null
    }
  }, [visible, token, apiId, apiHash, sessionName, onSuccess])

  // 刷新QR码
  const refreshQR = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'refresh' }))
    }
  }, [])

  // 提交2FA密码
  const submit2FA = useCallback(async () => {
    if (!password || !wsRef.current) return

    try {
      const encrypted = await encryptPassword(password)
      wsRef.current.send(JSON.stringify({
        type: '2fa',
        encrypted_password: encrypted
      }))
    } catch (err) {
      setError('密码加密失败')
    }
  }, [password, encryptPassword])

  // 关闭
  const handleClose = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'cancel' }))
      wsRef.current.close()
    }
    wsRef.current = null
    setState('connecting')
    setQrCode('')
    setError('')
    setPassword('')
    setTelegramUser(null)
    onClose()
  }, [onClose])

  // 打开时连接
  useEffect(() => {
    if (visible) {
      connect()
    }
    return () => {
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [visible, connect])

  const renderContent = () => {
    switch (state) {
      case 'connecting':
      case 'authenticating':
        return (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">
                {state === 'connecting' ? '正在连接...' : '正在初始化...'}
              </Text>
            </div>
          </div>
        )

      case 'qr_ready':
        return (
          <div style={{ textAlign: 'center' }}>
            <Title level={5}>使用Telegram扫描二维码</Title>
            <div style={{
              display: 'inline-block',
              padding: 16,
              background: '#fff',
              borderRadius: 8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}>
              {qrCode ? (
                <img
                  src={qrCode}
                  alt="Telegram QR Code"
                  style={{ width: 200, height: 200 }}
                />
              ) : (
                <div style={{ width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Spin />
                </div>
              )}
            </div>
            <div style={{ marginTop: 16 }}>
              <Button
                icon={<ReloadOutlined />}
                onClick={refreshQR}
              >
                刷新二维码
              </Button>
            </div>
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">
                打开Telegram App → 设置 → 设备 → 扫描二维码
              </Text>
            </div>
          </div>
        )

      case '2fa_required':
        return (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <LockOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
            <Title level={5}>需要两步验证</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
              请输入您的Telegram两步验证密码
            </Text>
            <Form layout="vertical" style={{ maxWidth: 300, margin: '0 auto' }}>
              <Form.Item>
                <Input.Password
                  size="large"
                  placeholder="两步验证密码"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onPressEnter={submit2FA}
                />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  size="large"
                  block
                  onClick={submit2FA}
                  disabled={!password}
                >
                  验证
                </Button>
              </Form.Item>
            </Form>
            <Alert
              type="info"
              showIcon
              message="密码安全"
              description="您的密码将被加密传输，不会被存储"
              style={{ marginTop: 16, textAlign: 'left' }}
            />
          </div>
        )

      case 'success':
        return (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />
            <Title level={4} style={{ marginTop: 16 }}>授权成功</Title>
            {telegramUser && (
              <div style={{ marginTop: 8 }}>
                <Text>
                  {telegramUser.first_name} {telegramUser.last_name}
                  {telegramUser.username && ` (@${telegramUser.username})`}
                </Text>
              </div>
            )}
          </div>
        )

      case 'error':
        return (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Alert
              type="error"
              showIcon
              message="授权失败"
              description={error}
              style={{ marginBottom: 16 }}
            />
            <Button type="primary" onClick={connect}>
              重试
            </Button>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <Modal
      title={
        <Space>
          <QrcodeOutlined />
          <span>Telegram账户授权</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      footer={
        state !== 'success' ? (
          <Button onClick={handleClose}>取消</Button>
        ) : null
      }
      width={400}
      destroyOnClose
    >
      {renderContent()}
    </Modal>
  )
}
